import React, {
  useState, useEffect, useCallback, useMemo, useRef
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStrings } from '../../i18n';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/adminService';
import { adminRetryShipment } from '../../services/shipmentService';
import { showCartToast, CartToastContainer } from '../../components/CartToast';
import ReconciliationMetrics from '../../components/ReconciliationMetrics';
import ReconciliationAlertPanel from '../../components/ReconciliationAlertPanel';
import ReconciliationFilters from '../../components/ReconciliationFilters';
import ShipmentBadge from '../../components/ShipmentBadge';
import RetryShipmentModal from '../../components/RetryShipmentModal';
import PanchTatvaButton from '../../components/PanchTatvaButton';

// ─── Thresholds ───────────────────────────────────────────────────────────────
const ORPHANED_MS          = 15 * 60 * 1000;   // 15 minutes (paid, no shipment)
const AUTO_REFRESH_MS      = 30_000;
const PAGE_SIZE            = 40;

// ─── Derived helpers ─────────────────────────────────────────────────────────

function minutesSince(ts) {
  if (!ts) return null;
  return Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
}

function deriveIsStuck(order) {
  if (order.shiprocket?.status !== 'processing') return false;
  const mins = minutesSince(order.shiprocket?.processing_started_at);
  return mins !== null && mins >= 10;
}

function deriveIsSlaBreached(order) {
  const deadline = order.shiprocket?.sla_deadline;
  if (!deadline) return false;
  if (order.shiprocket?.status === 'completed') return false;
  return new Date(deadline) < new Date();
}

function deriveIsOrphaned(order) {
  // Payment captured but no shipment started, older than threshold
  const isPaymentDone = order.status === 'confirmed' || order.status === 'shipped' || order.status === 'delivered';
  const hasNoShipment = !order.shiprocket?.status || order.shiprocket?.status === undefined;
  if (!isPaymentDone || !hasNoShipment) return false;
  const mins = minutesSince(order.createdAt);
  return mins !== null && mins * 60000 >= ORPHANED_MS;
}

/**
 * Returns the most important issue type for a given order.
 * Priority: permanent_failed > orphaned > stuck > sla_breach > failed_reconciling
 */
function deriveIssueType(order) {
  const status = order.shiprocket?.status;
  if (status === 'permanent_failed') return 'shipment_failed';
  if (deriveIsOrphaned(order))       return 'orphaned_payment';
  if (deriveIsStuck(order))          return 'stuck_processing';
  if (deriveIsSlaBreached(order))    return 'sla_breach';
  if (status === 'failed_reconciling') return 'shipment_retrying';
  return null;
}

function deriveMismatchType(order) {
  const issueType = deriveIssueType(order);
  if (!issueType) return null;
  if (issueType === 'orphaned_payment') return 'payment_missing_shipment';
  if (issueType === 'shipment_failed')  return 'shipment_failed';
  if (issueType === 'stuck_processing') return 'stale_processing';
  if (issueType === 'sla_breach')       return 'sla_breach';
  if (issueType === 'shipment_retrying') return 'shipment_failed';
  return null;
}

function deriveNeedsAttention(order) {
  return deriveIssueType(order) !== null;
}

// ─── Row highlight ────────────────────────────────────────────────────────────
function getRowBg(order) {
  const issueType = deriveIssueType(order);
  if (!issueType) return '';
  if (issueType === 'shipment_failed')   return 'bg-red-50';
  if (issueType === 'orphaned_payment')  return 'bg-purple-50/50';
  if (issueType === 'stuck_processing' || issueType === 'sla_breach') return 'bg-orange-50';
  if (issueType === 'shipment_retrying') return 'bg-orange-50/40';
  return '';
}

// ─── CSV export helper ────────────────────────────────────────────────────────
function exportCsv(orders) {
  const headers = ['Order ID','Status','Shipment Status','Issue Type','AWB','Courier','Failure Reason','Processing Min','SLA Deadline','Customer'];
  const rows = orders.map((o) => [
    o.orderId,
    o.status,
    o.shiprocket?.status || '',
    deriveIssueType(o) || '',
    o.shiprocket?.awb_code || '',
    o.shiprocket?.courier_name || '',
    o.shiprocket?.failure_reason || '',
    minutesSince(o.shiprocket?.processing_started_at) ?? '',
    o.shiprocket?.sla_deadline ? new Date(o.shiprocket.sla_deadline).toISOString() : '',
    o.customer?.email || '',
  ]);
  const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `reconciliation-monitor-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Issue type label helper ──────────────────────────────────────────────────
const ISSUE_LABELS = {
  orphaned_payment:  { text: 'Orphaned Payment', cls: 'bg-purple-100 text-purple-800' },
  shipment_failed:   { text: 'Perm. Failed',     cls: 'bg-red-100 text-red-800' },
  shipment_retrying: { text: 'Retrying',          cls: 'bg-orange-100 text-orange-800' },
  stuck_processing:  { text: 'Stuck',             cls: 'bg-yellow-100 text-yellow-800' },
  sla_breach:        { text: 'SLA Breach',        cls: 'bg-orange-200 text-orange-900' },
};

// ─── Mismatch helpers ────────────────────────────────────────────────────────
const SEVERITY_LEVELS  = ['critical', 'warning', 'info'];
const RESOLVE_ACTIONS  = [
  'adjust_inventory', 'issue_refund', 'cancel_order',
  'update_order_status', 'investigate', 'ignore', 'auto_fix',
];

function severityBadgeColor(s) {
  if (s === 'critical') return 'bg-red-600';
  if (s === 'warning')  return 'bg-yellow-600';
  if (s === 'info')     return 'bg-blue-600';
  return 'bg-gray-500';
}

function severityRowColor(s) {
  if (s === 'critical') return 'bg-red-50 border-red-200';
  if (s === 'warning')  return 'bg-yellow-50 border-yellow-200';
  if (s === 'info')     return 'bg-blue-50 border-blue-200';
  return 'bg-surface border-secondary/10';
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function AdminReconciliationMonitorV2() {
  const strings  = useStrings();
  const navigate = useNavigate();
  const { getIdToken } = useAuth();
  const s  = strings.reconciliationMonitor || {};
  const sa = strings.admin || {};

  // ── Active tab: 'health' | 'mismatches'
  const [activeTab, setActiveTab] = useState('health');

  // ── Live Health tab data
  const [orders, setOrders]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [error, setError]             = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);

  // ── Filters (health tab)
  const [issueFilter, setIssueFilter] = useState('all');
  const [timeFilter, setTimeFilter]   = useState('24h');

  // ── UI (health tab)
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [retryOrder, setRetryOrder]   = useState(null);
  const [expandedId, setExpandedId]   = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkRetrying, setBulkRetrying] = useState(false);
  const [reconRunning, setReconRunning] = useState(false);

  const intervalRef = useRef(null);

  // ── Mismatches tab data
  const [mismatches, setMismatches]           = useState([]);
  const [mmLoading, setMmLoading]             = useState(false);
  const [mmError, setMmError]                 = useState(null);
  const [mmPage, setMmPage]                   = useState(1);
  const [mmTotalPages, setMmTotalPages]       = useState(1);
  const [mmSummary, setMmSummary]             = useState(null);
  const [mmStatusFilter, setMmStatusFilter]   = useState('open');
  const [mmSeverityFilter, setMmSeverityFilter] = useState('all');
  const [mmRunning, setMmRunning]             = useState(false);
  const [selectedMismatch, setSelectedMismatch] = useState(null);
  const [showResolveForm, setShowResolveForm] = useState(false);
  const [resolveData, setResolveData]         = useState({ action: '', notes: '' });

  // ── Fetch live health orders ───────────────────────────────────────────────
  const fetchOrders = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      const idToken = await getIdToken();
      const params  = { page, limit: PAGE_SIZE };

      if (timeFilter === '1h')  params.since = new Date(Date.now() - 3_600_000).toISOString();
      if (timeFilter === '24h') params.since = new Date(Date.now() - 86_400_000).toISOString();

      const BACKEND_STATUS_MAP = { failedReconciling: 'failed_reconciling', permanentFailed: 'permanent_failed' };
      if (BACKEND_STATUS_MAP[issueFilter]) params.shipmentStatus = BACKEND_STATUS_MAP[issueFilter];

      const data = await adminAPI.orders.list(params, idToken);
      setOrders(data.orders ?? []);
      setTotalPages(data.pagination?.totalPages ?? 1);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getIdToken, page, timeFilter, issueFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Auto-refresh (health tab only)
  useEffect(() => {
    if (!autoRefresh) { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(() => fetchOrders({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, fetchOrders]);

  // ── Fetch mismatches ─────────────────────────────────────────────────────
  const fetchMismatches = useCallback(async () => {
    try {
      setMmLoading(true);
      setMmError(null);
      const idToken = await getIdToken();
      const params = new URLSearchParams({
        page: mmPage,
        limit: 10,
        status: mmStatusFilter,
        ...(mmSeverityFilter !== 'all' ? { severity: mmSeverityFilter } : {}),
      });
      const data = await adminAPI.reconciliation.list(params, idToken);
      setMismatches(data.mismatches ?? []);
      setMmTotalPages(data.pagination?.pages ?? 1);
      setMmSummary(data.summary ?? null);
    } catch (err) {
      setMmError(err.message);
    } finally {
      setMmLoading(false);
    }
  }, [getIdToken, mmPage, mmStatusFilter, mmSeverityFilter]);

  // Fetch mismatches when switching to mismatches tab (or when filters change)
  useEffect(() => {
    if (activeTab === 'mismatches') fetchMismatches();
  }, [activeTab, fetchMismatches]);

  // ── Run reconciliation (shared, used by both tabs) ───────────────────────
  const handleRunReconciliation = async () => {
    try {
      setReconRunning(true);
      setMmRunning(true);
      const idToken = await getIdToken();
      const data = await adminAPI.reconciliation.run({ types: ['inventory', 'payment', 'order'] }, idToken);
      showCartToast(`✓ Reconciliation done — ${data?.autoResolved ?? '?'} auto-resolved, ${data?.requiresManualReview ?? '?'} need review`);
      fetchOrders({ silent: true });
      if (activeTab === 'mismatches') { setMmPage(1); fetchMismatches(); }
    } catch (err) {
      showCartToast(`Error: ${err.message}`);
    } finally {
      setReconRunning(false);
      setMmRunning(false);
    }
  };

  // ── Resolve a mismatch ───────────────────────────────────────────────────
  const handleResolveMismatch = async (e) => {
    e.preventDefault();
    if (!selectedMismatch) return;
    try {
      const idToken = await getIdToken();
      await adminAPI.reconciliation.resolve(selectedMismatch._id, {
        action: resolveData.action,
        notes:  resolveData.notes,
      }, idToken);
      showCartToast('✓ Mismatch resolved');
      setShowResolveForm(false);
      setSelectedMismatch(null);
      setResolveData({ action: '', notes: '' });
      fetchMismatches();
    } catch (err) {
      showCartToast(`Error: ${err.message}`);
    }
  };

  // ── Derived metrics ──────────────────────────────────────────────────────
  const counts = useMemo(() => ({
    orphaned:          orders.filter(deriveIsOrphaned).length,
    failedReconciling: orders.filter((o) => o.shiprocket?.status === 'failed_reconciling').length,
    permanentFailed:   orders.filter((o) => o.shiprocket?.status === 'permanent_failed').length,
    stuck:             orders.filter(deriveIsStuck).length,
    slaBreach:         orders.filter(deriveIsSlaBreached).length,
    reconciledToday:   orders.filter((o) => o.shiprocket?.status === 'completed').length,
  }), [orders]);

  // ── Critical alerts ──────────────────────────────────────────────────────
  const alertItems = useMemo(() => {
    return orders
      .filter(deriveNeedsAttention)
      .map((order) => ({
        order,
        issueType: deriveIssueType(order),
        minutesSince: minutesSince(
          order.shiprocket?.processing_started_at || order.createdAt
        ),
      }))
      .sort((a, b) => {
        // Priority sort: permanent_failed > orphaned > stuck > sla > retrying
        const priority = {
          shipment_failed:   0,
          orphaned_payment:  1,
          stuck_processing:  2,
          sla_breach:        3,
          shipment_retrying: 4,
        };
        return (priority[a.issueType] ?? 9) - (priority[b.issueType] ?? 9);
      })
      .slice(0, 5);
  }, [orders]);

  // ── Front-end issue filter ────────────────────────────────────────────────
  const filteredOrders = useMemo(() => {
    let result = orders;
    if (issueFilter === 'orphaned')          result = orders.filter(deriveIsOrphaned);
    else if (issueFilter === 'stuck')        result = orders.filter(deriveIsStuck);
    else if (issueFilter === 'slaBreach')    result = orders.filter(deriveIsSlaBreached);
    else if (issueFilter === 'needsAttention') result = orders.filter(deriveNeedsAttention);
    // failedReconciling / permanentFailed already filtered by backend param
    return result;
  }, [orders, issueFilter]);

  // ── Bulk-selected permanent_failed orders ────────────────────────────────
  const bulkEligible = useMemo(
    () => filteredOrders.filter((o) => o.shiprocket?.status === 'permanent_failed'),
    [filteredOrders]
  );

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAll = () => {
    const eligibleIds = bulkEligible.map((o) => o._id);
    const allSelected = eligibleIds.every((id) => selectedIds.has(id));
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(eligibleIds));
  };

  // ── Bulk retry ───────────────────────────────────────────────────────────
  const handleBulkRetry = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Retry ${selectedIds.size} order(s)?`)) return;
    setBulkRetrying(true);
    const idToken = await getIdToken();
    let queued = 0;
    for (const id of selectedIds) {
      const order = orders.find((o) => o._id === id);
      if (!order) continue;
      try {
        await adminRetryShipment(order.orderId, 'Bulk retry from Reconciliation Monitor', idToken);
        queued++;
      } catch (_e) {
        // best-effort — continue other orders
      }
    }
    setBulkRetrying(false);
    setSelectedIds(new Set());
    showCartToast(`✓ ${queued} order(s) queued for retry`);
    fetchOrders({ silent: true });
  };

  // ── Retry single ─────────────────────────────────────────────────────────
  const handleRetryQueued = useCallback(() => {
    setRetryOrder(null);
    fetchOrders({ silent: true });
  }, [fetchOrders]);

  const handleFilterChange = (key) => { setIssueFilter(key); setPage(1); };
  const handleTimeChange   = (key) => { setTimeFilter(key);  setPage(1); };

  const fmtTime = (d) => d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">

      {/* Back button */}
      <Link to="/admin" className="inline-flex items-center text-secondary hover:text-secondary/80 mb-6 transition-colors">
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        {sa.backToDashboard || 'Back to Dashboard'}
      </Link>

      {/* ── Page header ── */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading text-headingText">
            {s.title || 'Reconciliation Monitor'}
          </h1>
          <p className="text-bodyText font-sans mt-1 text-sm">
            {s.subtitle || 'Live data-integrity dashboard — payment ↔ order ↔ shipment consistency'}
          </p>
        </div>

        {/* Shared controls */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Run reconciliation — always visible */}
          <button
            onClick={handleRunReconciliation}
            disabled={reconRunning || mmRunning}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-sans font-medium bg-secondary text-white hover:bg-secondary/90 disabled:opacity-50 transition-colors"
          >
            {(reconRunning || mmRunning) ? (s.reconRunning || 'Running…') : (s.runReconciliation || 'Run reconciliation')}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className="flex gap-1 mb-6 border-b border-secondary/20">
        {[
          { key: 'health',     label: s.tabHealth     || '🟢 Live Health',   desc: 'Orphaned payments, stuck orders, SLA breaches' },
          { key: 'mismatches', label: s.tabMismatches || '🔎 Mismatches',    desc: 'Data inconsistencies detected by reconciliation runs' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-2.5 text-sm font-sans font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-secondary text-secondary'
                : 'border-transparent text-textSecondary hover:text-bodyText'
            }`}
            title={tab.desc}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════
          TAB 1 — LIVE HEALTH
      ══════════════════════════════════════════ */}
      {activeTab === 'health' && (
        <>
          {/* Controls row */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            {/* Auto-refresh toggle */}
            <button
              onClick={() => setAutoRefresh((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-sans font-medium border transition-colors ${
                autoRefresh
                  ? 'bg-secondary/10 border-secondary text-secondary'
                  : 'bg-surface border-secondary/30 text-textSecondary'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-secondary animate-pulse' : 'bg-textSecondary/40'}`} />
              {s.autoRefresh || 'Auto-refresh'}: {autoRefresh ? (s.autoRefreshOn || 'ON') : (s.autoRefreshOff || 'OFF')}
            </button>

            {/* Manual refresh */}
            <button
              onClick={() => fetchOrders({ silent: true })}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-sans font-medium bg-cardBg border border-secondary/20 hover:border-secondary/50 text-bodyText disabled:opacity-50 transition-colors"
            >
              <svg className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {refreshing ? (s.refreshing || 'Refreshing…') : (s.manualRefresh || 'Refresh now')}
            </button>

            {/* Export CSV */}
            <button
              onClick={() => exportCsv(filteredOrders)}
              disabled={filteredOrders.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-sans font-medium bg-cardBg border border-secondary/20 hover:border-secondary/50 text-bodyText disabled:opacity-40 transition-colors"
            >
              📥 {s.exportCsv || 'Export CSV'}
            </button>

            {/* Last updated */}
            {lastUpdated && (
              <span className="text-xs font-sans text-textSecondary">
                {s.lastUpdated || 'Last updated'}: {fmtTime(lastUpdated)}
              </span>
            )}
          </div>

          {/* Error banner */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-sans">
              {error}
            </div>
          )}

          {/* Loading skeleton */}
          {loading && (
            <div className="space-y-4 mb-8">
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-cardBg rounded-2xl shadow-card h-24 animate-pulse" />
                ))}
              </div>
              <div className="bg-cardBg rounded-2xl shadow-card h-48 animate-pulse" />
            </div>
          )}

          {!loading && (
            <>
              <div className="mb-6">
                <ReconciliationMetrics counts={counts} activeFilter={issueFilter} onFilter={handleFilterChange} strings={s} />
              </div>
              <div className="mb-6">
                <ReconciliationAlertPanel alerts={alertItems} onRetry={setRetryOrder} onReconcile={handleRunReconciliation} strings={s} />
              </div>
              <div className="mb-4">
                <ReconciliationFilters issueFilter={issueFilter} timeFilter={timeFilter} onIssueFilter={handleFilterChange} onTimeFilter={handleTimeChange} resultCount={filteredOrders.length} strings={s} />
              </div>

              {/* Bulk actions bar */}
              {selectedIds.size > 0 && (
                <div className="mb-3 flex items-center gap-3 p-3 bg-secondary/10 border border-secondary/30 rounded-xl">
                  <span className="text-xs font-sans text-secondary font-medium">
                    {selectedIds.size} {s.selectedOrders || 'orders selected'}
                  </span>
                  <button onClick={handleBulkRetry} disabled={bulkRetrying}
                    className="px-3 py-1 text-xs font-sans font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors">
                    {bulkRetrying ? (s.bulkRetrying || 'Queuing…') : (s.bulkRetry || 'Retry all selected')}
                  </button>
                  <button onClick={() => setSelectedIds(new Set())}
                    className="text-xs font-sans text-textSecondary hover:text-bodyText transition-colors">
                    {s.clearSelection || 'Clear'}
                  </button>
                </div>
              )}

              {/* Order table */}
              {filteredOrders.length === 0 ? (
                <div className="bg-cardBg rounded-2xl shadow-card p-12 text-center">
                  <p className="text-bodyText font-sans text-lg">{s.noOrders || 'No orders match the current filters.'}</p>
                </div>
              ) : (
                <div className="bg-cardBg rounded-2xl shadow-card overflow-hidden border border-secondary/10">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-surface border-b border-secondary/20">
                        <tr>
                          <th className="px-3 py-3 w-8">
                            <input type="checkbox"
                              checked={bulkEligible.length > 0 && bulkEligible.every((o) => selectedIds.has(o._id))}
                              onChange={toggleSelectAll} className="cursor-pointer" title="Select all permanent_failed" />
                          </th>
                          <th className="px-2 py-3 w-6" />
                          <th className="px-3 py-3 text-left text-xs font-heading text-headingText">{s.colOrderId || 'Order ID'}</th>
                          <th className="px-3 py-3 text-left text-xs font-heading text-headingText">{s.colPayment || 'Payment'}</th>
                          <th className="px-3 py-3 text-left text-xs font-heading text-headingText">{s.colShipment || 'Shipment'}</th>
                          <th className="px-3 py-3 text-left text-xs font-heading text-headingText">{s.colIssueType || 'Issue Type'}</th>
                          <th className="px-3 py-3 text-left text-xs font-heading text-headingText">{s.colDuration || 'Processing'}</th>
                          <th className="px-3 py-3 text-left text-xs font-heading text-headingText">{s.colSla || 'SLA Deadline'}</th>
                          <th className="px-3 py-3 text-left text-xs font-heading text-headingText">{s.colLastUpdated || 'Last Updated'}</th>
                          <th className="px-3 py-3 text-left text-xs font-heading text-headingText">{s.colActions || 'Actions'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrders.map((order) => {
                          const issueType  = deriveIssueType(order);
                          const mismatch   = deriveMismatchType(order);
                          const stuck      = deriveIsStuck(order);
                          const slaBr      = deriveIsSlaBreached(order);
                          const duration   = minutesSince(order.shiprocket?.processing_started_at);
                          const sr         = order.shiprocket;
                          const rowBg      = getRowBg(order);
                          const isExpanded = expandedId === order._id;
                          const canRetry   = sr?.status === 'permanent_failed' || sr?.status === 'failed_reconciling';
                          const isSelected = selectedIds.has(order._id);
                          const issueLabel = issueType ? ISSUE_LABELS[issueType] : null;

                          return (
                            <React.Fragment key={order._id}>
                              <tr className={`border-b border-secondary/10 transition-colors duration-150 ${rowBg} hover:brightness-95`}>
                                <td className="px-3 py-3">
                                  {sr?.status === 'permanent_failed' && (
                                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(order._id)} className="cursor-pointer" />
                                  )}
                                </td>
                                <td className="px-2 py-3">
                                  <button onClick={() => setExpandedId(isExpanded ? null : order._id)}
                                    className="text-textSecondary hover:text-secondary transition-colors">
                                    <svg className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </button>
                                </td>
                                <td className="px-3 py-3">
                                  <p className="font-sans text-xs font-semibold text-headingText">{order.orderId}</p>
                                  <p className="font-sans text-xs text-textSecondary">
                                    {new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                  </p>
                                </td>
                                <td className="px-3 py-3">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-sans font-medium ${
                                    order.status === 'confirmed' || order.status === 'shipped' || order.status === 'delivered'
                                      ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                  }`}>{order.status?.replace(/_/g, ' ').toUpperCase() || '—'}</span>
                                </td>
                                <td className="px-3 py-3">
                                  {sr?.status ? <ShipmentBadge status={sr.status} /> : <span className="text-xs font-sans text-textSecondary">—</span>}
                                  {stuck && <span className="ml-1 inline-block px-1.5 py-0.5 rounded-full text-[10px] font-sans bg-red-100 text-red-700">STUCK</span>}
                                  {slaBr && <span className="ml-1 inline-block px-1.5 py-0.5 rounded-full text-[10px] font-sans bg-orange-100 text-orange-700">SLA ⚠</span>}
                                </td>
                                <td className="px-3 py-3">
                                  {issueLabel
                                    ? <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-sans font-medium ${issueLabel.cls}`}>{issueLabel.text}</span>
                                    : <span className="text-xs font-sans text-green-700">✓ OK</span>}
                                  {mismatch && <p className="text-[10px] font-sans text-textSecondary mt-0.5">{mismatch.replace(/_/g, ' ')}</p>}
                                </td>
                                <td className="px-3 py-3 font-sans text-xs">
                                  {duration !== null
                                    ? <span className={duration >= 10 ? 'text-red-700 font-medium' : 'text-bodyText'}>{duration}m</span>
                                    : <span className="text-textSecondary">—</span>}
                                </td>
                                <td className="px-3 py-3 font-sans text-xs">
                                  {sr?.sla_deadline
                                    ? <span className={slaBr ? 'text-red-700 font-medium' : 'text-bodyText'}>
                                        {new Date(sr.sla_deadline).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                      </span>
                                    : <span className="text-textSecondary">—</span>}
                                </td>
                                <td className="px-3 py-3 font-sans text-xs text-textSecondary">
                                  {order.updatedAt ? new Date(order.updatedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
                                </td>
                                <td className="px-3 py-3">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <button onClick={() => navigate(`/admin/orders?search=${order.orderId}`)}
                                      className="px-2 py-1 text-xs font-sans text-secondary hover:bg-secondary/10 rounded-lg transition-colors">
                                      {s.actionView || 'View'}
                                    </button>
                                    {canRetry && (
                                      <button onClick={() => setRetryOrder(order)}
                                        className="px-2 py-1 text-xs font-sans font-medium text-red-700 hover:bg-red-50 rounded-lg transition-colors">
                                        {s.actionRetry || 'Retry'}
                                      </button>
                                    )}
                                    <button onClick={handleRunReconciliation} disabled={reconRunning}
                                      className="px-2 py-1 text-xs font-sans text-orange-700 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-40">
                                      {s.actionReconcile || 'Reconcile'}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr className={`border-b border-secondary/10 ${rowBg}`}>
                                  <td colSpan={10} className="px-8 py-4">
                                    <ExpandedDetail order={order} strings={s} />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                    className="px-3 py-1.5 text-xs font-sans rounded-xl border border-secondary/20 bg-cardBg text-bodyText hover:border-secondary/50 disabled:opacity-40 transition-colors">
                    ← Prev
                  </button>
                  <span className="text-xs font-sans text-textSecondary">{page} / {totalPages}</span>
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="px-3 py-1.5 text-xs font-sans rounded-xl border border-secondary/20 bg-cardBg text-bodyText hover:border-secondary/50 disabled:opacity-40 transition-colors">
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════
          TAB 2 — MISMATCHES
      ══════════════════════════════════════════ */}
      {activeTab === 'mismatches' && (
        <>
          {/* Controls row */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <select value={mmStatusFilter} onChange={(e) => { setMmStatusFilter(e.target.value); setMmPage(1); }}
              className="px-3 py-2 border border-secondary/20 rounded-xl bg-surface text-headingText text-sm font-sans focus:outline-none focus:border-secondary">
              <option value="open">{sa.openIssues || 'Open Issues'}</option>
              <option value="resolved">{sa.resolved || 'Resolved'}</option>
              <option value="ignored">{sa.ignored || 'Ignored'}</option>
              <option value="all">{sa.allStatus || 'All'}</option>
            </select>

            <select value={mmSeverityFilter} onChange={(e) => { setMmSeverityFilter(e.target.value); setMmPage(1); }}
              className="px-3 py-2 border border-secondary/20 rounded-xl bg-surface text-headingText text-sm font-sans focus:outline-none focus:border-secondary">
              <option value="all">{sa.allSeverity || 'All Severity'}</option>
              {SEVERITY_LEVELS.map((lvl) => (
                <option key={lvl} value={lvl}>{lvl.charAt(0).toUpperCase() + lvl.slice(1)}</option>
              ))}
            </select>

            <button onClick={() => fetchMismatches()} disabled={mmLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-sans font-medium bg-cardBg border border-secondary/20 hover:border-secondary/50 text-bodyText disabled:opacity-50 transition-colors">
              <svg className={`w-3.5 h-3.5 ${mmLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {mmLoading ? 'Loading…' : (s.manualRefresh || 'Refresh')}
            </button>
          </div>

          {/* Summary cards */}
          {mmSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-cardBg rounded-xl shadow-card p-4 border border-secondary/10">
                <p className="text-xs text-textSecondary font-sans mb-1">{sa.openIssues || 'Open Issues'}</p>
                <p className="text-2xl font-heading text-headingText">{mmSummary.openCount}</p>
              </div>
              <div className="bg-cardBg rounded-xl shadow-card p-4 border border-red-200">
                <p className="text-xs text-textSecondary font-sans mb-1">{sa.critical || 'Critical'}</p>
                <p className="text-2xl font-heading text-red-600">{mmSummary.criticalCount}</p>
              </div>
              <div className="bg-cardBg rounded-xl shadow-card p-4 border border-yellow-200">
                <p className="text-xs text-textSecondary font-sans mb-1">{sa.warnings || 'Warnings'}</p>
                <p className="text-2xl font-heading text-yellow-600">{mmSummary.warningCount}</p>
              </div>
              <div className="bg-cardBg rounded-xl shadow-card p-4 border border-blue-200">
                <p className="text-xs text-textSecondary font-sans mb-1">{sa.info || 'Info'}</p>
                <p className="text-2xl font-heading text-blue-600">{mmSummary.infoCount}</p>
              </div>
            </div>
          )}

          {/* Error */}
          {mmError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-sans">{mmError}</div>
          )}

          {/* Loading */}
          {mmLoading && (
            <div className="space-y-3 mb-6">
              {[...Array(4)].map((_, i) => <div key={i} className="bg-cardBg rounded-2xl shadow-card h-28 animate-pulse" />)}
            </div>
          )}

          {/* Mismatch list */}
          {!mmLoading && !mmError && (
            mismatches.length === 0 ? (
              <div className="bg-cardBg rounded-2xl shadow-card p-12 text-center border border-secondary/10">
                <p className="text-bodyText font-sans text-lg">{sa.noIssuesFound || 'No issues found'}</p>
                <p className="text-textSecondary font-sans text-sm mt-2">
                  {sa.runReconciliationHint || 'Run Reconciliation above to scan for new issues.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4 mb-8">
                {mismatches.map((mm) => (
                  <div key={mm._id} className={`rounded-2xl shadow-card p-6 border-2 ${severityRowColor(mm.severity)}`}>
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-sans font-medium text-white ${severityBadgeColor(mm.severity)}`}>
                            {mm.severity?.toUpperCase()}
                          </span>
                          <span className="px-3 py-1 rounded-full text-xs font-sans font-medium bg-gray-200 text-gray-800">
                            {mm.type?.replace(/_/g, ' ').toUpperCase()}
                          </span>
                          {mm.status === 'resolved' && (
                            <span className="px-3 py-1 rounded-full text-xs font-sans font-medium bg-green-200 text-green-800">
                              ✓ {sa.resolved || 'Resolved'}
                            </span>
                          )}
                        </div>
                        <h3 className="text-base font-heading text-headingText mb-3">{mm.description}</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                          <div>
                            <p className="text-xs text-textSecondary font-sans mb-0.5">{sa.expected || 'Expected'}</p>
                            <p className="text-sm font-medium text-headingText font-sans">{mm.expectedValue}</p>
                          </div>
                          <div>
                            <p className="text-xs text-textSecondary font-sans mb-0.5">{sa.actual || 'Actual'}</p>
                            <p className="text-sm font-medium text-headingText font-sans">{mm.actualValue}</p>
                          </div>
                          <div>
                            <p className="text-xs text-textSecondary font-sans mb-0.5">{sa.difference || 'Difference'}</p>
                            <p className="text-sm font-medium text-red-600 font-sans">{mm.difference}</p>
                          </div>
                          <div>
                            <p className="text-xs text-textSecondary font-sans mb-0.5">{sa.detected || 'Detected'}</p>
                            <p className="text-xs text-bodyText font-sans">{new Date(mm.detectedAt).toLocaleDateString()}</p>
                          </div>
                        </div>
                        {mm.relatedProduct && (
                          <p className="text-sm text-bodyText font-sans">
                            <span className="font-medium">{sa.product || 'Product'}:</span> {mm.relatedProduct.name}
                          </p>
                        )}
                      </div>
                      {mm.status === 'open' && (
                        <button
                          onClick={() => { setSelectedMismatch(mm); setResolveData({ action: '', notes: '' }); setShowResolveForm(true); }}
                          className="px-5 py-2 bg-secondary text-white rounded-xl text-sm font-sans font-medium hover:bg-secondary/90 transition-colors whitespace-nowrap"
                        >
                          {sa.resolve || 'Resolve'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* Mismatch pagination */}
          {mmTotalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mb-4">
              <button onClick={() => setMmPage((p) => Math.max(1, p - 1))} disabled={mmPage === 1}
                className="px-3 py-1.5 text-xs font-sans rounded-xl border border-secondary/20 bg-cardBg text-bodyText hover:border-secondary/50 disabled:opacity-40 transition-colors">
                ← {sa.previous || 'Prev'}
              </button>
              <span className="text-xs font-sans text-textSecondary">{mmPage} / {mmTotalPages}</span>
              <button onClick={() => setMmPage((p) => Math.min(mmTotalPages, p + 1))} disabled={mmPage === mmTotalPages}
                className="px-3 py-1.5 text-xs font-sans rounded-xl border border-secondary/20 bg-cardBg text-bodyText hover:border-secondary/50 disabled:opacity-40 transition-colors">
                {sa.next || 'Next'} →
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Retry modal (Live Health tab) ── */}
      {retryOrder && (
        <RetryShipmentModal
          order={retryOrder}
          getIdToken={getIdToken}
          onClose={() => setRetryOrder(null)}
          onRetryQueued={handleRetryQueued}
          strings={strings.adminShipment || {}}
        />
      )}

      {/* ── Resolve mismatch modal (Mismatches tab) ── */}
      {showResolveForm && selectedMismatch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-cardBg rounded-2xl shadow-xl p-8 max-w-md w-full border border-secondary/10">
            <h2 className="text-2xl font-heading text-headingText mb-6">
              {sa.resolveMismatch || 'Resolve Mismatch'}
            </h2>
            <form onSubmit={handleResolveMismatch} className="space-y-5">
              <div className="bg-surface rounded-xl p-4 border border-secondary/10">
                <p className="text-xs text-textSecondary font-sans mb-1">{sa.issue || 'Issue'}</p>
                <p className="text-sm font-medium text-headingText font-sans">{selectedMismatch.description}</p>
                <p className="text-xs text-bodyText font-sans mt-1">
                  {selectedMismatch.expectedValue} → {selectedMismatch.actualValue}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-headingText font-sans mb-1.5">
                  {sa.action || 'Action'} *
                </label>
                <select required value={resolveData.action}
                  onChange={(e) => setResolveData({ ...resolveData, action: e.target.value })}
                  className="w-full px-4 py-3 border border-secondary/20 rounded-xl bg-surface text-headingText font-sans focus:outline-none focus:border-secondary">
                  <option value="">{sa.selectAction || 'Select action…'}</option>
                  {RESOLVE_ACTIONS.map((a) => (
                    <option key={a} value={a}>{a.replace(/_/g, ' ').toUpperCase()}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-headingText font-sans mb-1.5">
                  {sa.notes || 'Notes'}
                </label>
                <textarea value={resolveData.notes}
                  onChange={(e) => setResolveData({ ...resolveData, notes: e.target.value })}
                  className="w-full px-4 py-3 border border-secondary/20 rounded-xl bg-surface text-headingText font-sans focus:outline-none focus:border-secondary"
                  placeholder="Optional notes about this resolution" rows="3" />
              </div>
              <div className="flex gap-3 pt-2">
                <PanchTatvaButton variant="primary" size="large" type="submit" fullWidth>
                  {sa.confirm || 'Confirm'}
                </PanchTatvaButton>
                <PanchTatvaButton variant="outline" size="large" type="button" fullWidth
                  onClick={() => { setShowResolveForm(false); setSelectedMismatch(null); }}>
                  {sa.cancel || 'Cancel'}
                </PanchTatvaButton>
              </div>
            </form>
          </div>
        </div>
      )}

      <CartToastContainer />
    </div>
  );
}

// ─── Expanded detail sub-component ───────────────────────────────────────────
function ExpandedDetail({ order, strings: s = {} }) {
  const sr = order.shiprocket;
  const fmt = (ts) =>
    ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  const rows = sr ? [
    { label: 'Shipment Status',     value: sr.status || '—' },
    { label: 'Shipment ID',         value: sr.shipment_id || '—' },
    { label: 'AWB Code',            value: sr.awb_code || '—' },
    { label: 'Courier',             value: sr.courier_name || '—' },
    { label: 'Failure Reason',      value: sr.failure_reason || '—' },
    { label: 'Processing Started',  value: fmt(sr.processing_started_at) },
    { label: 'Locked At',           value: fmt(sr.locked_at) },
    { label: 'Failed At',           value: fmt(sr.failed_at) },
    { label: 'SLA Deadline',        value: fmt(sr.sla_deadline) },
    { label: 'Est. Delivery',       value: fmt(sr.estimated_delivery_date) },
    { label: 'Order Created',       value: fmt(order.createdAt) },
    { label: 'Order Updated',       value: fmt(order.updatedAt) },
  ] : [];

  return (
    <div>
      <p className="text-xs font-sans font-semibold text-textSecondary uppercase tracking-wide mb-3">
        {s.detailTitle || 'Full Record'}
      </p>
      {!sr ? (
        <p className="text-xs font-sans text-textSecondary">{s.noShipmentData || 'No shipment data available.'}</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-x-6 gap-y-3">
          {rows.map(({ label, value }) => (
            <div key={label}>
              <p className="text-xs text-textSecondary font-sans mb-0.5">{label}</p>
              <p className="text-xs font-sans text-headingText break-all">{value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
